import React from 'react'
import { Stack, Typography } from '@mui/material'
import AiCard, { AiCardDivider } from './cardKit/AiCard'
import DataRow from './cardKit/DataRow'
import { formatInquiryRupee, type HoldingsInquiry, type HoldingSnapshot } from './domains/tradingDomain'

type Props = {
    inquiry: HoldingsInquiry
}

function HoldingRow({ holding }: { holding: HoldingSnapshot }) {
    const average = formatInquiryRupee(holding.averageBuyPrice)
    const invested = formatInquiryRupee(holding.investedValue)
    const quantity = holding.t1Quantity ? `${holding.quantity} (+${holding.t1Quantity} T1)` : `${holding.quantity}`
    return (
        <Stack sx={{ mt: 0.75 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }}>
                    {holding.instrument.label}
                </Typography>
                <Typography variant="body2" fontWeight={600} className="font-feature-num">
                    {quantity}
                    {average ? ` @ ${average}` : ''}
                </Typography>
            </Stack>
            {invested ? (
                <Typography variant="caption" color="text.secondary" textAlign="right" className="font-feature-num">
                    Invested {invested}
                </Typography>
            ) : null}
        </Stack>
    )
}

/** Demat holdings ("what do I hold?"), distinct from the intraday/F&O position book. */
export default function HoldingsInquiryCard({ inquiry }: Props) {
    if (inquiry.outcome !== 'MATCHED' || inquiry.holdings.length === 0) {
        return null
    }
    const total = formatInquiryRupee(inquiry.totalInvestedValue)
    return (
        <AiCard accent="neutral">
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="subtitle2" fontWeight={700}>
                    Holdings
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {inquiry.holdings.length} stock{inquiry.holdings.length === 1 ? '' : 's'}
                </Typography>
            </Stack>
            {inquiry.holdings.slice(0, 8).map((holding) => (
                <HoldingRow key={`${holding.exchange}-${holding.securityCode}`} holding={holding} />
            ))}
            {inquiry.holdings.length > 8 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    +{inquiry.holdings.length - 8} more
                </Typography>
            ) : null}
            {total ? (
                <>
                    <AiCardDivider />
                    <DataRow label="Total invested" value={total} mono />
                </>
            ) : null}
        </AiCard>
    )
}
